const sprite = 'assets/sprite-green.svg';
const spriteBlack = 'assets/sprite-black.svg';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  ConfigurationService,
  Permissions,
  BootstrapConfiguration,
  Theme,
  IdService,
  LinkRegistryService,
  LinkRegistryConfiguration,
  LocaleConfiguration,
  Locale,
  RestConfiguration,
  ConsumerType,
  CONSUMERS,
  SystemStatus,
  BaseConfig,
  WebsocketConfiguration,
  UserPreferences,
  PreferencesService,
  AnalyticsConfiguration,
  OneTrustConfiguration,
  DataAnalyticsTaggingConfiguration,
  DataAnalyticsTaggingEvents,
  W8BenConfiguration,
  GamlConfiguration
} from '@tdwb/core';
import { AdDefinition, TargetedAdConfiguration } from '@tdwb/webbroker/core';
import { OfiOpenBankingConfiguration } from '@tdwb/lazy/transfers';

import { environment } from '../environments/environment';
import { WebbrokerDataAnalyticsTaggingConstants } from './data-analytics-tagging.constants';

/* eslint-disable @typescript-eslint/naming-convention */
const targetedAdTags: Record<string, Array<AdDefinition>> = {
  TAGS_EN: [{
    tagId: 16313143,
    sizes: [[1, 1]],
    targetId: 'wb-home-2accounts',
    native: {
      renderer_id: 357
    }
  }, {
    tagId: 16319533,
    sizes: [[1, 1]],
    targetId: 'wb-home-1account',
    native: {
      renderer_id: 358
    }
  }, {
    tagId: 16313141,
    sizes: [[1, 1]],
    targetId: 'wb-accounts-bottom',
    native: {
      renderer_id: 354
    }
  }, {
    tagId: 16313134,
    sizes: [[1, 1]],
    targetId: 'wb-balances-bottom',
    native: {
      renderer_id: 388
    }
  }, {
    tagId: 16313137,
    sizes: [[1, 1]],
    targetId: 'wb-transfers-end',
    native: {
      renderer_id: 356
    }
  }],
  TAGS_FR: [{
    tagId: 16319633,
    sizes: [[1, 1]],
    targetId: 'wb-home-2accounts',
    native: {
      renderer_id: 357
    }
  }, {
    tagId: 16319636,
    sizes: [[1, 1]],
    targetId: 'wb-home-1account',
    native: {
      renderer_id: 358
    }
  }, {
    tagId: 16319632,
    sizes: [[1, 1]],
    targetId: 'wb-accounts-bottom',
    native: {
      renderer_id: 354
    }
  }, {
    tagId: 16319602,
    sizes: [[1, 1]],
    targetId: 'wb-balances-bottom',
    native: {
      renderer_id: 388
    }
  }, {
    tagId: 16319630,
    sizes: [[1, 1]],
    targetId: 'wb-transfers-end',
    native: {
      renderer_id: 356
    }
  }],
  TAGS_EN_TEST: [{
    tagId: 16313008,
    sizes: [[1, 1]],
    targetId: 'wb-home-2accounts',
    native: {
      renderer_id: 1072
    }
  }, {
    tagId: 16313011,
    sizes: [[1, 1]],
    targetId: 'wb-home-1account',
    native: {
      renderer_id: 1071
    }
  }, {
    tagId: 16312981,
    sizes: [[1, 1]],
    targetId: 'wb-accounts-bottom',
    native: {
      renderer_id: 1074
    }
  }, {
    tagId: 16313004,
    sizes: [[1, 1]],
    targetId: 'wb-balances-bottom',
    native: {
      renderer_id: 1070
    }
  }, {
    tagId: 16312997,
    sizes: [[1, 1]],
    targetId: 'wb-transfers-end',
    native: {
      renderer_id: 1073
    }
  }],
  TAGS_FR_TEST: [{
    tagId: 16313126,
    sizes: [[1, 1]],
    targetId: 'wb-home-2accounts',
    native: {
      renderer_id: 1072
    }
  }, {
    tagId: 16313128,
    sizes: [[1, 1]],
    targetId: 'wb-home-1account',
    native: {
      renderer_id: 1071
    }
  }, {
    tagId: 16313023,
    sizes: [[1, 1]],
    targetId: 'wb-accounts-bottom',
    native: {
      renderer_id: 1074
    }
  }, {
    tagId: 16313123,
    sizes: [[1, 1]],
    targetId: 'wb-balances-bottom',
    native: {
      renderer_id: 1070
    }
  }, {
    tagId: 16313120,
    sizes: [[1, 1]],
    targetId: 'wb-transfers-end',
    native: {
      renderer_id: 1073
    }
  }]
};

export class WebbrokerConfig extends BaseConfig {
  // minimal config for core library
  private static readonly defaultConfig: Record<string, unknown> = {
    exportServiceUrl: '/',
    restConsumerName: 'com-td-wb-brk-webbroker',
    userPermissions: [],
    accountPermissions: [],
    systemStatus: SystemStatus.DOWN,
    menuConfig: {
      linkItems: {
        link_system_error: {
          type: 'client',
          url: '#/page/errors/500',
          permissions: []
        },
        global_td_financial_group: {
          url: 'https://www.td.com/',
          permissions: []
        },
        global_td_canada_trust: {
          url: 'https://www.tdcanadatrust.com/',
          permissions: []
        },
        global_td_easy_web: {
          url: 'https://easyweb.td.com/waw/idp/login.htm',
          permissions: []
        },
        error_state_root: {
          type: 'client',
          url: '#/page/errors',
          permissions: []
        }
      },
      topNavigation: [{
        id: '4',
        embeddedMarkup: false,
        icon: {
          cssClass: 'td-wb-icon--padlock'
        },
        text: 'Logout',
        href: '/waw/brk/wb/services/rest/v1/session/logout',
        target: '_self'
      }],
      utilityToolbar: [],
      globalNavigation: [],
      localNavigation: []
    }
  };

  /**
   * getBootstrapConfig
   * Prepares site with minimal default data before configuration GET call
   *
   * @param document the global document object
   * @param consumerType defined previously. usually webbroker but special case is for attrader agreements
   * @returns the minimal BootstrapConfig object
   */
  static getBootstrapConfig(document: Document, consumerType: ConsumerType): BootstrapConfiguration {
    const applicationId = `com-td-brk-wb-${consumerType.consumerId}`;
    return {
      configEndpoint: {
        url: environment.configEndpoint,
        applicationId,
        headers: {
          'x-broker-rest-version': 'v1.0',
          'x-consumer-name': applicationId,
          'x-correlation-id': IdService.getUUID()
        }
      },
      defaultConfig: WebbrokerConfig.defaultConfig,
      locale: BaseConfig.getLocaleFromLocation(document),
      theme: WebbrokerConfig.selectTheme(document),
      unsupportedBrowserUrl: 'https://www.td.com/ca/en/investing/direct-investing/trading/webbroker' // TODO: create a page
    };
  }

  static getThemes(): Array<Theme> {
    return [{
      name: 'green',
      themeClass: 'td-wb-theme--green',
      spriteUrl: sprite
    }, {
      name: 'black',
      themeClass: 'td-wb-theme--black',
      spriteUrl: spriteBlack
    }];
  }

  // TODO experimental for emerald standards style alignment
  static selectTheme(document: Document): string {
    const defaultTheme = 'green';
    const allThemes = WebbrokerConfig.getThemes().map((entry: Theme) => entry.name);
    const params = new URLSearchParams(document.location?.search);
    const theme = (params.get('theme') || defaultTheme).replace('/', '');
    return allThemes.indexOf(theme) === -1 ? defaultTheme : theme;
  }

  static getLocaleConfiguration(): Array<LocaleConfiguration> {
    return [
      {
        locale: Locale.en_CA,
        async loadChildren<T>(): Promise<T> {
          const m = await import('./webbroker.locale.en.module');
          return m.WebbrokerLocaleEnModule as T;
        }
      },
      {
        locale: Locale.fr_CA,
        async loadChildren<T>(): Promise<T> {
          const m = await import('./webbroker.locale.fr.module');
          return m.WebbrokerLocaleFrModule as T;
        }
      }
    ];
  }

  static getBaseUrl(configurationService: ConfigurationService): string {
    return environment.base === undefined ?
      configurationService.getProperty('resourceLinksBaseUrl') :
      environment.base;
  }

  static getRestConfig(configurationService: ConfigurationService): RestConfiguration {
    return {
      baseUrl: WebbrokerConfig.getBaseUrl(configurationService),
      resourceConfiguration: configurationService.getProperty('resourceLinks')
    };
  }

  static getSecurityConfig(configurationService: ConfigurationService): Permissions {
    const userPermissions = configurationService.getProperty('userPermissions');
    const accountPermissions = configurationService.getProperty('accountPermissions');
    const urlPermissions = LinkRegistryService.getUrlPermissions(WebbrokerConfig.getLinkRegistryConfig(configurationService));
    return { userPermissions, accountPermissions, urlPermissions } as Permissions;
  }

  static getLinkRegistryConfig(configurationService: ConfigurationService): LinkRegistryConfiguration {
    const linkItems = configurationService.getProperty<LinkRegistryConfiguration>('menuConfig.linkItems');
    linkItems['$$$_common_core_test_state_root'] = {
      type: 'client',
      url: '#/page/components',
      permissions: []
    };
    linkItems['$$$_webbroker_core_test_state_root'] = {
      type: 'client',
      url: '#/page/webbroker',
      permissions: []
    };
    return linkItems;
  }

  static getTargetedAdConfig(configurationService: ConfigurationService): TargetedAdConfiguration {
    const urlString = configurationService.getProperty<string>('websocketUrl');
    const prodTags = urlString === undefined || urlString.match(/(delta|omega|pat)\.td\.com/gi) === null;
    let tags: Array<AdDefinition>;
    if (prodTags) {
      tags = configurationService.getProperty('locale') === 'fr_CA' ? targetedAdTags.TAGS_FR : targetedAdTags.TAGS_EN;
    } else {
      tags = configurationService.getProperty('locale') === 'fr_CA' ? targetedAdTags.TAGS_FR_TEST : targetedAdTags.TAGS_EN_TEST;
    }
    return {
      dcxServiceEnabled: configurationService.getProperty('dcxServiceEnabled'),
      tags
    };
  }

  static getWebsocketUrl(configurationService: ConfigurationService, preferencesService: PreferencesService): WebsocketConfiguration {
    const url = environment !== undefined && environment.websocketBase !== undefined ?
      environment.websocketBase : configurationService.getProperty('websocketUrl');
    const sessionTimeoutCallback = ((): Observable<number> => preferencesService.getUserPreferences()
      .pipe(map((userPreference: UserPreferences): number => {
        let timeout: number;
        if (typeof userPreference.SESSION_TIMEOUT !== 'undefined') {
          timeout = userPreference.SESSION_TIMEOUT as number;
        }
        return timeout;
      }))
    );
    return {
      url,
      sessionTimeoutCallback
    } as WebsocketConfiguration;
  }

  /**
   * getConsumer: retrieves information of how the site is going to be used
   * Webbroker: regular application. consumer will be 'webbroker' and not embedded on all scenarions
   *    except when attrader is defined at the url
   * Attrader: special use case where webbroker is embedded to let attrader user sign agreements
   *    will happen when attrader is present at the url and will always be embedded
   *
   * @returns ConsumerType
   */
  static getConsumer(): ConsumerType {
    const url = window.location.href;
    const isEmbedded = url.includes('consumer=attrader');
    const consumerId = isEmbedded ?
      CONSUMERS.CONSUMER_ID_ATTRADER :
      CONSUMERS.CONSUMER_ID_WEB_BROKER;

    return {
      consumerId,
      isEmbedded
    };
  }

  static getAnalyticsConfig(configurationService: ConfigurationService): AnalyticsConfiguration {
    return {
      analyticsUrl: configurationService.getProperty('analyticsUrl'),
      analyticsDisabled: configurationService.getProperty('analyticsDisabled')
    };
  }

  static getOneTrustConfig(configurationService: ConfigurationService): OneTrustConfiguration {
    return {
      oneTrustUrl: configurationService.getProperty('oneTrustUrl'),
      oneTrustDomainScript: configurationService.getProperty('oneTrustDomainScript'),
      oneTrustEnabled: configurationService.getProperty('oneTrustEnabled')
    };
  }

  static getDataAnalyticsTaggingConfig(configurationService: ConfigurationService): DataAnalyticsTaggingConfiguration {
    const dataLayerScriptUrl = configurationService.getProperty<string>('dataLayerUrl');
    const environment =
      !dataLayerScriptUrl?.includes('staging') &&
      !dataLayerScriptUrl?.includes('development')
        ? 'production'
        : 'non-production';

    const mapper =
      WebbrokerDataAnalyticsTaggingConstants
        .dataAnalyticsTaggingEventArr
        .reduce((allEvents: DataAnalyticsTaggingEvents, currModule: DataAnalyticsTaggingEvents) => {
          Object.keys(currModule).reduce((acc: DataAnalyticsTaggingEvents, key: string) => {
            const keyTransformed =
              key.replace(/([A-Z])/g, (f:string) => {
                return `.${f.toLowerCase()}`;
              }).replace(/_\./g, () => {return '-';});
            acc[`${keyTransformed}`] = currModule[key];
            return acc;
          }, allEvents);
          return allEvents
        }, {});
    return {
      buildTag: BaseConfig.getBuildTag(configurationService),
      locale: BaseConfig.getLocale(configurationService),
      environment,
      dataLayerScriptUrl,
      dataLayerEnabled: configurationService.getProperty('dataLayerEnabled'),
      routeStateTaggingEventMapper: mapper
    };
  }

  static getW8BenConfig(configurationService: ConfigurationService): W8BenConfiguration {
    return {
      enabled: !!configurationService.getProperty('featureFlags.w8Ben')
    }
  }

  static getGamlConfig(configurationService: ConfigurationService): GamlConfiguration {
    return {
      gamlWFLFeature: Boolean(configurationService.getProperty('featureFlags.GAML_WFL')),
      gamlMVP1FixFeature: Boolean(configurationService.getProperty('featureFlags.gamlMVP1FixEnabled')),
      gamlTriggered: configurationService.getProperty('userPreferences.GAML_INDICATOR'),
      gamlHaltTrade: configurationService.getProperty('userPreferences.GAML_HALT_TRADE_INDICATOR')
    }
  }

  static getOpenBankingConfig(configurationService: ConfigurationService): OfiOpenBankingConfiguration {
    return {
      openBankingDataUserInterfaceUrl: configurationService.getProperty('openBankingDataUserInterfaceUrl')
    };
  }
}
